import { LOCKABLE_TYPES } from "@corca/tiptap";

import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { Node as PMNode, ResolvedPos } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";

export function isLockable(node: PMNode | null | undefined): boolean {
  return !!node && LOCKABLE_TYPES.includes(node.type.name);
}

export function isLocked(node: PMNode | null | undefined): boolean {
  return !!node && node.attrs.locked === true;
}

/** Is `$pos` inside a locked top-level (depth-1) block? */
export function isInLockedTopBlock($pos: ResolvedPos): boolean {
  return $pos.depth >= 1 && isLocked($pos.node(1));
}

/**
 * A collapsed range only counts when strictly inside a locked block's content;
 * insertion points at boundaries between blocks stay allowed.
 */
export function rangeTouchesLockedBlock(doc: PMNode, from: number, to: number): boolean {
  if (from === to) {
    return isInLockedTopBlock(doc.resolve(from));
  }

  const $from = doc.resolve(from);
  const $to = doc.resolve(to);
  // A depth-0 `to` sits between blocks and does not touch the following one.
  const last = $to.depth === 0 ? $to.index(0) - 1 : $to.index(0);

  for (let i = $from.index(0); i <= last; i++) {
    if (isLocked(doc.child(i))) {
      return true;
    }
  }

  return false;
}

export function selectionTouchesLockedBlock(state: EditorState): boolean {
  return state.selection.ranges.some((range) =>
    rangeTouchesLockedBlock(state.doc, range.$from.pos, range.$to.pos),
  );
}

/**
 * Ids of the MobX roots (expression wrappers, graphs) that live inside locked top-level blocks.
 * Mirrored into `EditorModel.lockedRootIds` so the MobX side can gate edits reactively.
 */
export function collectLockedRootIds(doc: PMNode): Set<string> {
  const ids = new Set<string>();

  doc.forEach((block) => {
    if (!isLocked(block)) {
      return;
    }

    if (block.type.name === "graph" && typeof block.attrs.graphId === "string") {
      ids.add(block.attrs.graphId);
    }

    block.descendants((pmNode) => {
      if (nodeIsExpression(pmNode) && typeof pmNode.attrs.nodeId === "string") {
        ids.add(pmNode.attrs.nodeId);
      }
    });
  });

  return ids;
}

/** Sidebar edits go through MobX and bypass the PM guard; nodes in locked blocks are read-only. */
export function isNodeEditable(node: NodeModel): boolean {
  return node.doc.editable && !isModelNodeInLockedBlock(node);
}

/** Is this math node model rendered inside a locked top-level block (as an expression or a graph)? */
export function isModelNodeInLockedBlock(node: NodeModel): boolean {
  const root = isLockRoot(node) ? node : node.ancestors.find(isLockRoot);

  return !!root && root.locked;
}

function isLockRoot(node: NodeModel): node is WrapperNodeModel | GraphNodeModel {
  return node.is(WrapperNodeModel) || node.is(GraphNodeModel);
}

export function isSelectionLocked(doc: DocumentModel): boolean {
  const state = doc.editor.tiptap?.state;

  if (state && selectionTouchesLockedBlock(state)) {
    return true;
  }

  if (doc.cursor.node && isModelNodeInLockedBlock(doc.cursor.node)) {
    return true;
  }

  return doc.selection.allSelectedNodes.some(isModelNodeInLockedBlock);
}
